import { useRef, useState } from "react";
import NextIcon from "Assets/Img/NextIcon";
import Button from "../../../../Components/Common/Button/Button";
import SectionCard from "./SectionCard";
import SignaturePad, {
  SignaturePadHandle,
} from "Components/Common/SignaturePad/SignaturePad";
import PrevIcon from "Assets/Img/PrevIcon";
import WaitingIcon from "Assets/Img/WaitingIcon";
import StepTick from "Assets/Img/StepTick";
import { uploadFileToS3 } from "../../GcPreconCommercial/UploadFileToS3";
import { formatDate } from "utils/Common/Date";

interface ChecklistItem {
  id: number;
  status: "done" | "pending" | "inprogress";
  title: string;
  subtitle: string;
}

interface SummaryItem {
  label: string;
  value: string;
  accent?: boolean;
}

interface ReviewSubmitStepProps {
  summary: SummaryItem[];
  gcSigned: boolean;
  allInvoicesAttached: boolean;
  onGcSignChange: (signed: boolean) => void;
  onBack: () => void;
  onSubmit: (gcSignatureKey: string) => Promise<void>;
  submitError?: string;
}

const ReviewSubmitStep = ({
  summary,
  gcSigned,
  allInvoicesAttached,
  onGcSignChange,
  onBack,
  onSubmit,
  submitError,
}: ReviewSubmitStepProps) => {
  const padRef = useRef<SignaturePadHandle>(null);
  const [submitting, setSubmitting] = useState(false);
  const [signError, setSignError] = useState(false);
  const checklistItems: ChecklistItem[] = [
    {
      id: 1,
      status: "done",
      title: "Draw request form completed",
      subtitle: `Draw ${summary[2]?.value} | ${summary[3]?.value} | Period to ${formatDate(summary[5]?.value, false)}`,
    },
    {
      id: 2,
      status: allInvoicesAttached ? "done" : "pending",
      title: "Invoices attached for all line items",
      subtitle: allInvoicesAttached
        ? "All invoices attached"
        : "Pending uploads must be completed before final release",
    },
    {
      id: 3,
      status: "pending",
      title: `Lien waivers collected (required by ${summary[1]?.value})`,
      subtitle: "Outstanding waivers may block disbursement",
    },
    {
      id: 4,
      status: "inprogress",
      title: "CSC field inspection and progress report",
      subtitle: "Triggered automatically after submission",
    },
  ];
  const handleSubmitClick = async () => {
    if (!padRef.current?.isSigned()) {
      setSignError(true);
      const sigEl = document.querySelector<HTMLElement>(
        ".signatureGrid canvas",
      );
      sigEl
        ?.closest(".signatureGrid")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      sigEl?.focus();
      return;
    }
    setSignError(false);
    setSubmitting(true);
    try {
      let gcSignatureKey = "";
      if (padRef.current?.isSigned()) {
        const blob = await padRef.current.getBlob();
        const file = new File([blob], "gc-signature.png", {
          type: "image/png",
        });
        const result: any = await uploadFileToS3(file);
        gcSignatureKey = result?.key ?? result?.Key ?? "";
      }
      await onSubmit(gcSignatureKey);
    } catch (err) {
      console.error("err", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="screen activeScreen"
      aria-labelledby="review-submit-title"
    >
      <h2 className="sectionTitle" id="review-submit-title">
        Review &amp; Submit
      </h2>
      <p className="sectionSub">
        Confirm all requirements are met, then sign and submit the draw request
        to CSC New England and {summary[1]?.value}.
      </p>

      <SectionCard title="Submission Checklist">
        {checklistItems.map((item) => (
          <div className="checkItem" key={item.id}>
            <div className={`checkIcon ${item.status}`.trim()}>
              {item.status === "done" ? (
                <StepTick />
              ) : item.status === "inprogress" ? (
                <WaitingIcon />
              ) : (
                "!"
              )}
            </div>
            <div className="checkText">
              <p className="checkLabel">{item.title}</p>
              <p className="checkSub">{item.subtitle}</p>
            </div>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Draw Summary">
        <div className="summaryGrid">
          {summary.map((item) => (
            <div className="summaryItem" key={item.label}>
              <span>{item.label}</span>
              <strong className={item.accent ? "accent" : ""}>
                {item.value}
              </strong>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Signatures Required">
        <p className="signatureDescription">
          By signing below, you certify that all materials and labor described
          are correct in quantity and amount, were furnished only to the job
          described, and there have been no changes affecting the construction
          budget or appraised value that the lender has not been notified of.
        </p>

        <div className="signatureGrid">
          <SignaturePad
            ref={padRef}
            label="Builder / GC Signature"
            statusId="gcSignatureStatus"
            error={signError}
            onSignedChange={(signed) => {
              onGcSignChange(signed);
              if (signed) setSignError(false);
            }}
          />
        </div>

        <div className="disclaimerBox">
          Consultant Services New England LLC is NOT responsible for signature
          verification on Waivers of Lien. Item # is taken from Column A of the
          AIA.
        </div>
      </SectionCard>

      {submitError && (
        <div className="errorBox" role="alert">
          {submitError}
        </div>
      )}

      <div className="btnRow spaced">
        <Button
          className="btn btnSecondary"
          onClick={onBack}
          disabled={submitting}
        >
          <PrevIcon /> Back
        </Button>
        <Button
          className="btn btnSuccess"
          onClick={handleSubmitClick}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <div className="btn-spinner" />
              Submitting{" "}
            </>
          ) : (
            <>
              Submit Draw Request
              <NextIcon />
            </>
          )}
        </Button>
      </div>
    </section>
  );
};

export default ReviewSubmitStep;
